/**
 * Pure, session-local interval-merging utilities for tracking genuinely
 * covered video playback ranges.
 *
 * KAN-306 round 5: the previous accrual model was a blind running SUM of
 * small per-tick deltas. Live QA proved this lets a learner replay an
 * already-watched short segment indefinitely to rack up unlimited credit
 * (each replay tick re-adds its delta, since a running sum has no concept
 * of "already counted"), defeating watch-percentage compliance gating --
 * QA reproduced a case where replaying 0:00-0:51 repeatedly pushed the
 * credited total past 100% while ~2 minutes of the video were never
 * watched at all.
 *
 * Tracking merged [start, end] intervals for the CURRENT SESSION instead
 * means replaying a range already inside an existing interval contributes
 * ZERO additional seconds -- credit can only grow by covering genuinely NEW
 * ground.
 *
 * Residual approximation (documented, accepted): intervals are tracked only
 * for the CURRENT session, not persisted as a set (no DB schema change --
 * the backend still only stores a single scalar seconds value). Replaying a
 * section that was covered in a PRIOR session (not this one) can still add
 * credit on top of the persisted seed, since that prior session's exact
 * coverage isn't reconstructable from a scalar. This is a strictly smaller,
 * bounded window than the previous exploit (unbounded replay within a
 * single sitting).
 *
 * @requirements REQ-VIDEO-107
 */

export interface WatchedInterval {
  start: number;
  end: number;
}

/**
 * Merge a newly-covered [start, end) range into an existing, already-merged
 * list of intervals. Returns a NEW array (does not mutate the input).
 * Overlapping or touching (adjacent) intervals are absorbed into one.
 */
export function mergeWatchedInterval(
  intervals: WatchedInterval[],
  start: number,
  end: number
): WatchedInterval[] {
  if (!(end > start)) {
    return intervals;
  }

  let newStart = start;
  let newEnd = end;
  const untouched: WatchedInterval[] = [];

  for (const iv of intervals) {
    const overlapsOrAdjacent = iv.end >= newStart && iv.start <= newEnd;
    if (overlapsOrAdjacent) {
      newStart = Math.min(newStart, iv.start);
      newEnd = Math.max(newEnd, iv.end);
    } else {
      untouched.push(iv);
    }
  }

  const result = [...untouched, { start: newStart, end: newEnd }];
  result.sort((a, b) => a.start - b.start);
  return result;
}

/** Sum of the (non-overlapping, already-merged) interval lengths. */
export function sumWatchedIntervals(intervals: WatchedInterval[]): number {
  return intervals.reduce((total, iv) => total + (iv.end - iv.start), 0);
}
